/**
 * Scene layout types — the output of mapping a topology to physical placements.
 *
 * Campus-based composition: scenes define buildings on a grass ground plane,
 * connected by stone paths. campusToLayout() flattens them into the SceneLayout
 * consumed by the renderer.
 */

// ---------------------------------------------------------------------------
// Low-level layout types (consumed by PixelWorldCanvas entity construction)
// ---------------------------------------------------------------------------

/** Grid placement for an agent within a scene. */
export interface AgentPlacement {
  agentId: string;
  gridCol: number;
  gridRow: number;
  deskType: "large" | "small";
  isEntryPoint: boolean;
}

/** Furniture decoration placement. */
export interface FurniturePlacement {
  type:
    | "plant" | "water-cooler" | "bookshelf" | "conveyor" | "judge-bench" | "podium"
    | "filing-cabinet" | "whiteboard" | "rug" | "coffee-mug" | "paper-stack" | "monitor-bank"
    | "server-rack" | "comms-station" | "inbox-tray" | "bulletin-board"
    | "tree" | "bench" | "bush" | "flower";
  gridCol: number;
  gridRow: number;
}

/** Wall segment placement. */
export interface WallPlacement {
  side: "nw" | "ne";
  gridCol: number;
  gridRow: number;
}

/** Floor tile area definition. */
export interface FloorAreaDef {
  tileType: "office" | "factory" | "stage" | "manager" | "break" | "grass" | "path";
  startCol: number;
  startRow: number;
  width: number;
  height: number;
}

/** Complete scene layout computed from a topology definition. */
export interface SceneLayout {
  agents: AgentPlacement[];
  furniture: FurniturePlacement[];
  walls: WallPlacement[];
  floors: FloorAreaDef[];
  bounds: {
    minCol: number;
    minRow: number;
    maxCol: number;
    maxRow: number;
  };
}

// ---------------------------------------------------------------------------
// Campus-based composition types
// ---------------------------------------------------------------------------

/** Cell type in the campus grid. */
export type CellType = "grass" | "path" | "office" | "manager" | "factory" | "stage" | "break";

/** Agent placement within a building (relative to building origin). */
export interface BuildingAgentDef {
  agentId: string;
  localCol: number;
  localRow: number;
  deskType: "large" | "small";
  isEntryPoint: boolean;
}

/** A standalone building on the campus. */
export interface BuildingFootprint {
  id: string;
  col: number;
  row: number;
  width: number;
  height: number;
  floorType: CellType;
  agents: BuildingAgentDef[];
  furniture: FurniturePlacement[];
}

/** Campus layout definition — input to campusToLayout(). */
export interface CampusDef {
  buildings: BuildingFootprint[];
  pathCells: Array<{ col: number; row: number }>;
  decorations: FurniturePlacement[];
}

// ---------------------------------------------------------------------------
// Campus layout builder
// ---------------------------------------------------------------------------

/**
 * Generate walls on all 4 edges of a standalone rectangular building.
 * Skip wall segments where paths cross the building boundary (doorways).
 */
export function generateBuildingWalls(
  b: BuildingFootprint,
  pathCells?: Set<string>,
): WallPlacement[] {
  const walls: WallPlacement[] = [];

  // Helper: check if a path cell is adjacent to this wall position on the outside
  const hasDoor = (wallCol: number, wallRow: number, edge: "west" | "east" | "north" | "south"): boolean => {
    if (!pathCells) return false;
    switch (edge) {
      case "west":  return pathCells.has(`${wallCol - 1},${wallRow}`);
      case "east":  return pathCells.has(`${wallCol + 1},${wallRow}`);
      case "north": return pathCells.has(`${wallCol},${wallRow - 1}`);
      case "south": return pathCells.has(`${wallCol},${wallRow + 1}`);
    }
  };

  // NW walls along the west (left) edge
  for (let r = 0; r < b.height; r++) {
    if (!hasDoor(b.col, b.row + r, "west")) {
      walls.push({ side: "nw", gridCol: b.col, gridRow: b.row + r });
    }
  }
  // NE walls along the north (top) edge
  for (let c = 0; c < b.width; c++) {
    if (!hasDoor(b.col + c, b.row, "north")) {
      walls.push({ side: "ne", gridCol: b.col + c, gridRow: b.row });
    }
  }
  // NW walls along the east (right) edge — forms SE boundary
  for (let r = 0; r < b.height; r++) {
    const edgeCol = b.col + b.width - 1;
    if (!hasDoor(edgeCol, b.row + r, "east")) {
      walls.push({ side: "nw", gridCol: b.col + b.width, gridRow: b.row + r });
    }
  }
  // NE walls along the south (bottom) edge — forms SW boundary
  for (let c = 0; c < b.width; c++) {
    const edgeRow = b.row + b.height - 1;
    if (!hasDoor(b.col + c, edgeRow, "south")) {
      walls.push({ side: "ne", gridCol: b.col + c, gridRow: b.row + b.height });
    }
  }
  return walls;
}

/** Route an L-shaped, 2-tile-wide path between two buildings. */
export function routePath(
  from: BuildingFootprint,
  to: BuildingFootprint,
): Array<{ col: number; row: number }> {
  const fromCenterCol = from.col + Math.floor(from.width / 2);
  const fromCenterRow = from.row + Math.floor(from.height / 2);
  const toCenterCol = to.col + Math.floor(to.width / 2);
  const toCenterRow = to.row + Math.floor(to.height / 2);

  const cells: Array<{ col: number; row: number }> = [];

  // Horizontal segment
  const minC = Math.min(fromCenterCol, toCenterCol);
  const maxC = Math.max(fromCenterCol, toCenterCol);
  for (let c = minC; c <= maxC; c++) {
    cells.push({ col: c, row: fromCenterRow });
    cells.push({ col: c, row: fromCenterRow + 1 });
  }

  // Vertical segment
  const minR = Math.min(fromCenterRow, toCenterRow);
  const maxR = Math.max(fromCenterRow, toCenterRow);
  for (let r = minR; r <= maxR; r++) {
    cells.push({ col: toCenterCol, row: r });
    cells.push({ col: toCenterCol + 1, row: r });
  }

  return cells;
}

/**
 * Scatter outdoor decorations (trees, bushes, flowers) on remaining grass cells.
 * Avoids cells occupied by buildings, paths, or within 1 cell of buildings.
 */
export function scatterDecorations(
  gridWidth: number,
  gridHeight: number,
  originCol: number,
  originRow: number,
  occupied: Set<string>,
  nearBuilding: Set<string>,
): FurniturePlacement[] {
  const decorations: FurniturePlacement[] = [];
  // Weighted toward trees: tree, tree, tree, bush, bush, flower
  const decorTypes: FurniturePlacement["type"][] = ["tree", "tree", "tree", "bush", "bush", "flower"];

  for (let r = 0; r < gridHeight; r++) {
    for (let c = 0; c < gridWidth; c++) {
      const col = originCol + c;
      const row = originRow + r;
      const key = `${col},${row}`;

      // Skip occupied and near-building cells
      if (occupied.has(key) || nearBuilding.has(key)) continue;

      // Hash-based scatter: ~15% density — trees dominate the landscape
      const hash = (col * 17 + row * 31) % 7;
      if (hash === 0) {
        const typeIdx = (col * 7 + row * 11) % decorTypes.length;
        decorations.push({ type: decorTypes[typeIdx], gridCol: col, gridRow: row });
      }
    }
  }

  return decorations;
}

/**
 * Convert a CampusDef into a SceneLayout using a cell grid approach.
 *
 * 1. Fill bounding box + padding with grass
 * 2. Stamp building floors (overwriting grass)
 * 3. Stamp path cells (overwriting grass, but NOT building interiors)
 * 4. Run-length encode the grid into FloorAreaDef[]
 * 5. Generate per-building walls
 * 6. Collect furniture, agents, decorations
 */
export function campusToLayout(campus: CampusDef, padding = 3): SceneLayout {
  // --- 1. Compute bounds ---
  let minCol = Infinity, minRow = Infinity, maxCol = -Infinity, maxRow = -Infinity;
  for (const b of campus.buildings) {
    minCol = Math.min(minCol, b.col);
    minRow = Math.min(minRow, b.row);
    maxCol = Math.max(maxCol, b.col + b.width - 1);
    maxRow = Math.max(maxRow, b.row + b.height - 1);
  }
  for (const p of campus.pathCells) {
    minCol = Math.min(minCol, p.col);
    minRow = Math.min(minRow, p.row);
    maxCol = Math.max(maxCol, p.col);
    maxRow = Math.max(maxRow, p.row);
  }

  // Add padding
  minCol -= padding;
  minRow -= padding;
  maxCol += padding;
  maxRow += padding;

  const gridWidth = maxCol - minCol + 1;
  const gridHeight = maxRow - minRow + 1;

  // --- 2. Create cell grid filled with grass ---
  type CT = FloorAreaDef["tileType"];
  const grid: CT[][] = Array.from({ length: gridHeight }, () =>
    new Array<CT>(gridWidth).fill("grass"),
  );

  // Helper to set a cell
  const setCell = (col: number, row: number, type: CT) => {
    const gc = col - minCol;
    const gr = row - minRow;
    if (gc >= 0 && gc < gridWidth && gr >= 0 && gr < gridHeight) {
      grid[gr][gc] = type;
    }
  };

  // Track which cells are inside buildings (so paths don't overwrite them)
  const buildingCells = new Set<string>();
  const occupied = new Set<string>(); // buildings + paths

  // --- 3. Stamp building floors ---
  for (const b of campus.buildings) {
    for (let r = 0; r < b.height; r++) {
      for (let c = 0; c < b.width; c++) {
        const col = b.col + c;
        const row = b.row + r;
        setCell(col, row, b.floorType as CT);
        buildingCells.add(`${col},${row}`);
        occupied.add(`${col},${row}`);
      }
    }
  }

  // --- 4. Stamp path cells (only on grass, not on building interiors) ---
  for (const p of campus.pathCells) {
    if (!buildingCells.has(`${p.col},${p.row}`)) {
      setCell(p.col, p.row, "path");
      occupied.add(`${p.col},${p.row}`);
    }
  }

  // --- 5. Run-length encode grid into FloorAreaDef[] ---
  const floors: FloorAreaDef[] = [];
  for (let gr = 0; gr < gridHeight; gr++) {
    let runStart = 0;
    let runType = grid[gr][0];
    for (let gc = 1; gc <= gridWidth; gc++) {
      const cellType = gc < gridWidth ? grid[gr][gc] : null;
      if (cellType !== runType) {
        floors.push({
          tileType: runType,
          startCol: minCol + runStart,
          startRow: minRow + gr,
          width: gc - runStart,
          height: 1,
        });
        if (gc < gridWidth) {
          runStart = gc;
          runType = grid[gr][gc];
        }
      }
    }
  }

  // --- 6. Generate walls per building (with door gaps where paths meet edges) ---
  const pathCellSet = new Set<string>();
  for (const p of campus.pathCells) {
    pathCellSet.add(`${p.col},${p.row}`);
  }
  const walls: WallPlacement[] = [];
  for (const b of campus.buildings) {
    walls.push(...generateBuildingWalls(b, pathCellSet));
  }

  // --- 7. Collect agents and furniture ---
  const agents: AgentPlacement[] = [];
  const furniture: FurniturePlacement[] = [];

  for (const b of campus.buildings) {
    for (const a of b.agents) {
      agents.push({
        agentId: a.agentId,
        gridCol: b.col + a.localCol,
        gridRow: b.row + a.localRow,
        deskType: a.deskType,
        isEntryPoint: a.isEntryPoint,
      });
    }
    furniture.push(...b.furniture);
  }

  // --- 8. Scatter outdoor decorations ---
  const nearBuilding = new Set<string>();
  for (const b of campus.buildings) {
    for (let r = -1; r <= b.height; r++) {
      for (let c = -1; c <= b.width; c++) {
        nearBuilding.add(`${b.col + c},${b.row + r}`);
      }
    }
  }
  const scattered = scatterDecorations(gridWidth, gridHeight, minCol, minRow, occupied, nearBuilding);
  furniture.push(...scattered);
  furniture.push(...campus.decorations);

  return {
    agents,
    furniture,
    walls,
    floors,
    bounds: { minCol, minRow, maxCol, maxRow },
  };
}

// ---------------------------------------------------------------------------
// Topology info
// ---------------------------------------------------------------------------

/** Minimal topology info needed for scene layout computation. */
export interface TopologyInfo {
  type: string;
  agents: Array<{
    id: string;
    name?: string;
    backend?: string;
  }>;
  edges: Array<{
    from: string;
    to: string;
    type: string;
    bidirectional?: boolean;
  }>;
  entryPoint?: string;
  exitPoints?: string[];
}
